'use client';

import dynamic from 'next/dynamic';
import type React from 'react';
import { PortalContextProvider } from '@/common/Portal/PortalContext';
import { searchProject } from '~/constants/eventable';
import AnalyticsProvider from '~/context/common/AnalyticsProvider';
import {
  AppliedFiltersProvider,
  type OwnProps as AppliedFiltersContextProps
} from '~/context/common/AppliedFiltersContext';
import {
  ConditionalDeviceContextProvider,
  type OwnProps as DeviceContextProps
} from '~/context/common/DeviceContext';
import { type PropsFromServer as ExperimentContextProps } from '~/context/common/ExperimentProvider';
import {
  FacetDataContextProvider,
  type OwnProps as FacetDataContextProps
} from '~/context/common/FacetDataContext';
import { FactoryUpgradesQualiProvider } from '~/context/common/FactoryUpgradesQualiContext';
import {
  NativeContextProvider,
  type OwnProps as NativeContextProps
} from '~/context/common/NativeContext';
import { SavedSearchesContextProvider } from '~/context/common/SavedSearchesContext';
import { SebastianContextProvider } from '~/context/common/SebastianContext';
import {
  StaticContextProvider as CommonStaticContextProvider,
  type OwnProps as CommonStaticContextProps
} from '~/context/common/StaticContext';
import {
  TradeContextProvider as CommonTradeContextProvider,
  type OwnProps as CommonTradeContextProps
} from '~/context/common/TradeContext';
import {
  UserContextProvider,
  type OwnProps as UserContextProps
} from '~/context/common/UserContext';
import { ErrorContextProvider } from '~/context/srp/ErrorContext';
import {
  FinanceContextProvider,
  type FinanceContextProps
} from '~/context/srp/FinanceContext';
import {
  InventoryContextProvider,
  type OwnProps as InventoryContextProps
} from '~/context/srp/InventoryContext';
import { ModalContextProvider } from '~/context/srp/ModalContext';
import { PricingContextProvider } from '~/context/srp/PricingContext';
import { QualiContextProvider } from '~/context/srp/QualiContext';
import {
  RouteContextProvider,
  type OwnProps as RouteContextProps
} from '~/context/srp/RouteContext';
import {
  SearchContextProvider,
  type OwnProps as SearchContextProps
} from '~/context/srp/SearchContext';
import {
  SearchEventsDataContextProvider,
  type OwnProps as SearchEventDataContextProps
} from '~/context/srp/SearchEventsDataContext';
import {
  SEOContextProvider,
  type OwnProps as SEOContextProps
} from '~/context/srp/SEOContext';
import { ConditionalSrpExperimentProvider } from '~/context/srp/SrpExperimentProvider';
import {
  StaticContextProvider,
  type OwnProps as StaticContextProps
} from '~/context/srp/StaticContext';
import { SuggestedFiltersContextProvider } from '~/context/srp/SuggestedFiltersContext';

const CoreAuth = dynamic(
  () => import(/* webpackChunkName: "CoreAuth" */ '@/common/CoreAuth'),
  { ssr: false }
);

export type SearchProviderProps = React.PropsWithChildren<{
  forAnalyticsContext: {
    correlationId: string;
    payloadMetaAttributes: {
      dealershipId?: string;
      isWebview: boolean;
      webview: {
        platform: 'ios' | 'android' | null;
        version: string;
      } | null;
    };
    track: string;
  };
  forAppliedFiltersContext: AppliedFiltersContextProps;
  forCommonStaticContext: CommonStaticContextProps;
  forCommonTradeContext: CommonTradeContextProps;
  forDeviceContext?: DeviceContextProps;
  forExperimentContext?: ExperimentContextProps;
  forFacetDataContext: FacetDataContextProps;
  forFinanceContext: FinanceContextProps;
  forInventoryContext: InventoryContextProps;
  forNativeContext: NativeContextProps;
  forRouteContext: RouteContextProps;
  forSearchContext: SearchContextProps;
  forSearchEventDataContext: SearchEventDataContextProps;
  forSebastianContext?: { isSebastianLoadedOnNative: boolean };
  forSEOContext: SEOContextProps;
  forStaticContext: StaticContextProps;
  forUserContext: UserContextProps;
}>;

export default function SearchProviders({
  children,
  forAnalyticsContext,
  forAppliedFiltersContext,
  forCommonStaticContext,
  forCommonTradeContext,
  forDeviceContext,
  forExperimentContext,
  forFacetDataContext,
  forFinanceContext,
  forInventoryContext,
  forNativeContext,
  forRouteContext,
  forSearchContext,
  forSearchEventDataContext,
  forSebastianContext,
  forSEOContext,
  forStaticContext,
  forUserContext
}: SearchProviderProps) {
  return (
    <CommonStaticContextProvider {...forCommonStaticContext}>
      <StaticContextProvider {...forStaticContext}>
        <NativeContextProvider {...forNativeContext}>
          <ConditionalSrpExperimentProvider
            experimentContext={forExperimentContext}
          >
            <AnalyticsProvider
              projectName={searchProject}
              {...forAnalyticsContext}
            >
              <ConditionalDeviceContextProvider
                deviceContext={forDeviceContext}
              >
                <UserContextProvider {...forUserContext}>
                  <SearchContextProvider {...forSearchContext}>
                    <PricingContextProvider>
                      <RouteContextProvider {...forRouteContext}>
                        <SEOContextProvider {...forSEOContext}>
                          <FacetDataContextProvider {...forFacetDataContext}>
                            <SuggestedFiltersContextProvider>
                              <ErrorContextProvider>
                                <SebastianContextProvider
                                  isSebastianLoadedOnNative={
                                    forSebastianContext?.isSebastianLoadedOnNative
                                  }
                                >
                                  <InventoryContextProvider
                                    {...forInventoryContext}
                                  >
                                    <FinanceContextProvider
                                      {...forFinanceContext}
                                    >
                                      <CommonTradeContextProvider
                                        {...forCommonTradeContext}
                                      >
                                        <AppliedFiltersProvider
                                          {...forAppliedFiltersContext}
                                        >
                                          <SearchEventsDataContextProvider
                                            {...forSearchEventDataContext}
                                          >
                                            <PortalContextProvider>
                                              <ModalContextProvider>
                                                <SavedSearchesContextProvider>
                                                  <QualiContextProvider>
                                                    <FactoryUpgradesQualiProvider>
                                                      <CoreAuth />
                                                      {children}
                                                    </FactoryUpgradesQualiProvider>
                                                  </QualiContextProvider>
                                                </SavedSearchesContextProvider>
                                              </ModalContextProvider>
                                            </PortalContextProvider>
                                          </SearchEventsDataContextProvider>
                                        </AppliedFiltersProvider>
                                      </CommonTradeContextProvider>
                                    </FinanceContextProvider>
                                  </InventoryContextProvider>
                                </SebastianContextProvider>
                              </ErrorContextProvider>
                            </SuggestedFiltersContextProvider>
                          </FacetDataContextProvider>
                        </SEOContextProvider>
                      </RouteContextProvider>
                    </PricingContextProvider>
                  </SearchContextProvider>
                </UserContextProvider>
              </ConditionalDeviceContextProvider>
            </AnalyticsProvider>
          </ConditionalSrpExperimentProvider>
        </NativeContextProvider>
      </StaticContextProvider>
    </CommonStaticContextProvider>
  );
}
